import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import './MagicLinkCallback.css'; // Reuse existing sleek styling
import Logo from '../../../components/Logo/Logo';

import { applyHandoffContinuation, getPendingHandoffToken } from '../../edu-landing/handoffContinuation';
import { continuePendingCheckoutOrChat } from '../continueAfterSignIn';
import { oauthRedirectUri } from '../oauthRedirect';
import { withAppBasename } from '../../../config/appBase';
import {
  clearPasswordChangeSignIn,
  recordPasswordChangeSignIn,
} from '../passwordChangeSignIn';

const GoogleCallback = () => {
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code');
  const returnedState = searchParams.get('state');
  const errorParam = searchParams.get('error');
  
  const navigate = useNavigate();
  const { loginWithGoogle } = useAuth();
  
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('Initializing verification...');
  const [isVisible, setIsVisible] = useState(false);
  const startedRef = useRef(false);

  useEffect(() => {
    // Same re-run as LinkedInCallback: a new loginWithGoogle from
    // AuthProvider must not repeat the one-time state check.
    if (startedRef.current) return;
    startedRef.current = true;

    setIsVisible(true);
    
    const verifySSO = async () => {
      // 1. Check for error params sent by Google
      if (errorParam) {
        setError(`Google auth error: ${errorParam}. Please try again.`);
        return;
      }

      if (!code) {
        setError('No authorization code was returned from Google. Please try signing in again.');
        return;
      }

      // 2. CSRF Protection: Verify state token matches what was stored in sessionStorage
      const savedState = sessionStorage.getItem('google_oauth_state');
      sessionStorage.removeItem('google_oauth_state'); // Consume immediately

      if (!returnedState || returnedState !== savedState) {
        setError('Security check failed: OAuth state mismatch. This might be a cross-site request forgery attempt. Please try signing in again.');
        return;
      }
      
      try {
        setStatus('Verifying your Google credentials and logging in...');
        // Must equal the redirect_uri Login.js sent to Google, or the code exchange fails.
        const redirectUri = oauthRedirectUri('google');
        const pendingHandoffToken = getPendingHandoffToken();

        recordPasswordChangeSignIn({ source: 'google' });
        const result = await loginWithGoogle(code, redirectUri, true, pendingHandoffToken);

        if (result.success) {
          applyHandoffContinuation(result);
          if (result.user?.must_change_password) {
            setStatus('Authentication successful! Logging you in...');
            navigate('/set-password', { replace: true });
            return;
          }
          clearPasswordChangeSignIn();
          setStatus('Authentication successful! Logging you in...');
          setTimeout(() => {
            continuePendingCheckoutOrChat({ navigate, user: result.user });
          }, 800);
        } else {
          clearPasswordChangeSignIn();
          setError(result.error || 'Google SSO verification failed. Please try login again.');
        }
      } catch (err) {
        clearPasswordChangeSignIn();
        console.error('Google Callback authentication failure:', err);
        setError('An unexpected error occurred during Google authentication. Please try again.');
      }
    };

    verifySSO();
  }, [code, returnedState, errorParam, loginWithGoogle, navigate]);

  return (
    <div className="login-page">
      <div className="login-background">
        <div className="login-overlay"></div>
      </div>
      
      <div className="login-container">
        <div className={`login-content ${isVisible ? 'fade-in-up' : ''}`} style={{ textAlign: 'center', padding: '50px 40px' }}>
          <div className="login-header" style={{ justifyContent: 'center', marginBottom: '24px' }}>
            <div className="login-logo">
              <a
                href={withAppBasename('/')}
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/');
                }}
                style={{
                  textDecoration: 'none',
                  fontFamily: 'var(--iv-font-head)',
                  fontSize: '24px',
                  fontWeight: 700,
                  color: 'var(--iv-head)',
                  letterSpacing: '-0.02em',
                  display: 'inline-flex',
                  alignItems: 'baseline',
                  gap: '6px'
                }}
              >
                IVORI <span style={{ fontFamily: 'var(--iv-font-body)', fontSize: '15px', fontWeight: 600, color: 'var(--iv-body)' }}>Edu</span>
              </a>
            </div>
          </div>
          
          <div className="callback-card-content">
            {error ? (
              <div className="error-state">
                <div className="error-icon" style={{ 
                  color: 'var(--iv-danger)', 
                  fontSize: '36px', 
                  marginBottom: '16px' 
                }}>
                  ⚠
                </div>
                <h2 style={{ fontFamily: 'var(--iv-font-head)', color: 'var(--iv-body)', marginBottom: '10px', fontSize: '22px', fontWeight: '700' }}>
                  Verification Failed
                </h2>
                <p style={{ color: 'var(--iv-muted)', marginBottom: '28px', fontSize: '14px', lineHeight: '1.5' }}>
                  {error}
                </p>
                <button 
                  onClick={() => navigate('/login')}
                  style={{
                    width: '100%',
                    backgroundColor: 'var(--iv-btn)',
                    color: 'var(--iv-btn-fg)',
                    border: '1px solid var(--iv-btn)',
                    padding: '12px 24px',
                    borderRadius: '999px',
                    fontWeight: 600,
                    fontSize: '14px',
                    cursor: 'pointer',
                    fontFamily: 'var(--iv-font-body)'
                  }}
                >
                  Back to Sign In
                </button>
              </div>
            ) : (
              <div className="loading-state">
                <div className="premium-spinner-container">
                  <div className="outer-pulse-ring"></div>
                  <div className="inner-rotating-ring"></div>
                </div>
                <h3 style={{ fontFamily: 'var(--iv-font-head)', color: 'var(--iv-head)', fontSize: '20px', fontWeight: '600', marginBottom: '8px' }}>
                  Google SSO Authentication
                </h3>
                <p style={{ color: 'var(--iv-muted)', fontSize: '14px' }}>
                  {status}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GoogleCallback;
